import {
  buildAuthorizeUrl,
  codeChallengeFromVerifier,
  randomUrlSafeString,
  readCallbackParams,
} from '../lib/pkce'
import { isRecord, readJson, removeKey, writeJson } from '../lib/storage'

/**
 * A minimal Authorization Code + PKCE client, shared by the two independent
 * logins. Tokens are held client-side only; the backend never receives a
 * Spotify token (and has no use for one).
 */

export interface TokenSet {
  accessToken: string
  refreshToken: string | null
  idToken: string | null
  /** Epoch milliseconds. */
  expiresAt: number
}

export interface OAuthClientOptions {
  authorizeEndpoint: string
  tokenEndpoint: string
  clientId: string
  redirectUri: string
  scopes: readonly string[]
  /** Prefix for the localStorage keys of this client. */
  storageNamespace: string
  extraAuthorizeParams?: Record<string, string>
}

export class OAuthError extends Error {
  constructor(
    message: string,
    readonly code: 'state_mismatch' | 'denied' | 'exchange_failed' | 'no_session',
  ) {
    super(message)
    this.name = 'OAuthError'
  }
}

/** Refresh this long before actual expiry so an in-flight request never 401s. */
const EXPIRY_MARGIN_MS = 60_000

function isTokenSet(value: unknown): value is TokenSet {
  return (
    isRecord(value) &&
    typeof value.accessToken === 'string' &&
    typeof value.expiresAt === 'number' &&
    (value.refreshToken === null || typeof value.refreshToken === 'string') &&
    (value.idToken === null || typeof value.idToken === 'string')
  )
}

interface TokenResponse {
  access_token?: unknown
  refresh_token?: unknown
  id_token?: unknown
  expires_in?: unknown
}

export function createOAuthClient(options: OAuthClientOptions) {
  const tokenKey = `${options.storageNamespace}.tokens`
  const verifierKey = `${options.storageNamespace}.verifier`
  const stateKey = `${options.storageNamespace}.state`
  const returnToKey = `${options.storageNamespace}.returnTo`

  function loadTokens(): TokenSet | null {
    return readJson(tokenKey, isTokenSet)
  }

  function storeTokens(tokens: TokenSet): void {
    writeJson(tokenKey, tokens)
  }

  function clear(): void {
    removeKey(tokenKey)
    removeKey(verifierKey)
    removeKey(stateKey)
  }

  function toTokenSet(payload: unknown, previous: TokenSet | null): TokenSet {
    if (!isRecord(payload)) throw new OAuthError('Malformed token response', 'exchange_failed')
    const body = payload as TokenResponse
    if (typeof body.access_token !== 'string') {
      throw new OAuthError('Token response had no access token', 'exchange_failed')
    }
    const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : 3600
    return {
      accessToken: body.access_token,
      refreshToken:
        typeof body.refresh_token === 'string'
          ? body.refresh_token
          : (previous?.refreshToken ?? null),
      idToken: typeof body.id_token === 'string' ? body.id_token : (previous?.idToken ?? null),
      expiresAt: Date.now() + expiresIn * 1000,
    }
  }

  async function post(body: Record<string, string>): Promise<unknown> {
    const response = await fetch(options.tokenEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(body).toString(),
    })
    if (!response.ok) {
      throw new OAuthError(`Token endpoint responded ${response.status}`, 'exchange_failed')
    }
    return response.json()
  }

  return {
    isConnected(): boolean {
      return loadTokens() !== null
    },

    tokens: loadTokens,

    /** Where the user was before being sent to the identity provider. */
    takeReturnTo(): string | null {
      const stored = readJson(
        returnToKey,
        (value): value is { path: string } => isRecord(value) && typeof value.path === 'string',
      )
      removeKey(returnToKey)
      return stored?.path ?? null
    },

    async beginLogin(returnTo?: string): Promise<string> {
      const verifier = randomUrlSafeString()
      const state = randomUrlSafeString(16)
      writeJson(verifierKey, { verifier })
      writeJson(stateKey, { state })
      if (returnTo) writeJson(returnToKey, { path: returnTo })
      return buildAuthorizeUrl(options.authorizeEndpoint, {
        response_type: 'code',
        client_id: options.clientId,
        redirect_uri: options.redirectUri,
        scope: options.scopes.join(' '),
        code_challenge_method: 'S256',
        code_challenge: await codeChallengeFromVerifier(verifier),
        state,
        ...options.extraAuthorizeParams,
      })
    },

    async completeLogin(search: string): Promise<TokenSet> {
      const params = readCallbackParams(search)
      if (params.error) {
        throw new OAuthError(params.errorDescription ?? params.error, 'denied')
      }
      const expectedState = readJson(
        stateKey,
        (value): value is { state: string } => isRecord(value) && typeof value.state === 'string',
      )
      if (!params.state || params.state !== expectedState?.state) {
        throw new OAuthError('State did not match', 'state_mismatch')
      }
      const stored = readJson(
        verifierKey,
        (value): value is { verifier: string } =>
          isRecord(value) && typeof value.verifier === 'string',
      )
      if (!params.code || !stored) {
        throw new OAuthError('Authorization code or verifier missing', 'exchange_failed')
      }
      const payload = await post({
        grant_type: 'authorization_code',
        client_id: options.clientId,
        code: params.code,
        redirect_uri: options.redirectUri,
        code_verifier: stored.verifier,
      })
      const tokens = toTokenSet(payload, null)
      storeTokens(tokens)
      removeKey(verifierKey)
      removeKey(stateKey)
      return tokens
    },

    /** Returns a valid access token, refreshing when it is close to expiry. */
    async getAccessToken(): Promise<string> {
      const current = loadTokens()
      if (!current) throw new OAuthError('Not connected', 'no_session')
      if (current.expiresAt - EXPIRY_MARGIN_MS > Date.now()) return current.accessToken
      if (!current.refreshToken) {
        clear()
        throw new OAuthError('Session expired and cannot be refreshed', 'no_session')
      }
      try {
        const payload = await post({
          grant_type: 'refresh_token',
          client_id: options.clientId,
          refresh_token: current.refreshToken,
        })
        const refreshed = toTokenSet(payload, current)
        storeTokens(refreshed)
        return refreshed.accessToken
      } catch (error) {
        clear()
        if (error instanceof OAuthError) throw new OAuthError(error.message, 'no_session')
        throw new OAuthError('Refresh failed', 'no_session')
      }
    },

    logout: clear,
  }
}

export type OAuthClient = ReturnType<typeof createOAuthClient>

/** Reads the (already trusted) claims of a JWT for display purposes only. */
export function readJwtClaims(token: string): Record<string, unknown> | null {
  const parts = token.split('.')
  const payload = parts[1]
  if (parts.length !== 3 || payload === undefined) return null
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const parsed: unknown = JSON.parse(json)
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}
